import { ADEQUATIONS, LIBELLES_ADEQUATION, type Adequation } from './adequation.js';
import type { CandidatureResume } from './dossiers.js';
import { LIBELLES_SOURCE } from './exports.js';
import { aRelancer } from './suivi.js';

/** Pastille qui vaut l'étoile « potentiel élevé » (décision du 2026-09-15 : posée d'office, rien à cocher). */
export const POTENTIEL_ETOILE = 5;

export function estEtoile(potentiel: number | null | undefined): boolean {
  return potentiel === POTENTIEL_ETOILE;
}

/**
 * Filtres rapides de la liste par annonce (docs/UI.md § Écran 1). Partagés :
 * l'export Excel rend exactement ce que l'écran montre sous le même filtre.
 */
export const FILTRES_LISTE = ['toutes', 'etoiles', 'a_contacter', 'a_relancer', 'rdv', 'cv_illisible', 'ecartes'] as const;
export type FiltreListe = (typeof FILTRES_LISTE)[number];

export const LIBELLES_FILTRE_LISTE: Record<FiltreListe, string> = {
  toutes: 'Toutes',
  etoiles: 'Étoilés',
  a_contacter: 'À contacter',
  a_relancer: 'À relancer',
  rdv: 'Rendez-vous pris',
  cv_illisible: 'CV illisible',
  ecartes: 'Écartés',
};

export function estFiltreListe(valeur: unknown): valeur is FiltreListe {
  return typeof valeur === 'string' && (FILTRES_LISTE as readonly string[]).includes(valeur);
}

export function correspondAuFiltre(
  filtre: FiltreListe,
  candidature: CandidatureResume,
  maintenant: Date = new Date(),
): boolean {
  switch (filtre) {
    case 'toutes':
      return true;
    case 'etoiles':
      return estEtoile(candidature.potentiel);
    case 'a_contacter':
      return candidature.statut === 'nouveau' || candidature.statut === 'a_contacter';
    case 'a_relancer':
      return aRelancer(candidature, maintenant);
    case 'rdv':
      return candidature.statut === 'rdv_planifie';
    case 'cv_illisible':
      return candidature.cvIllisible;
    case 'ecartes':
      return candidature.statut === 'ecarte' || candidature.statut === 'desistement';
  }
}

// ---------------------------------------------------------------------------
// Critères combinables (demande du 2026-09-17)
// ---------------------------------------------------------------------------
// Ils s'ajoutent au filtre rapide. Jamais d'âge, de sexe, de situation
// familiale ni de santé : ni stockés, ni critères (CLAUDE.md § Interdits).

export const CRITERES_CV = ['tous', 'avec', 'sans'] as const;
export type CritereCv = (typeof CRITERES_CV)[number];

export const LIBELLES_CRITERE_CV: Record<CritereCv, string> = {
  tous: 'Avec ou sans CV',
  avec: 'CV joint',
  sans: 'Sans CV',
};

/** Valeur du critère « ville » pour les fiches qui n'en ont pas. */
export const VILLE_NON_RENSEIGNEE = '-';

export interface CriteresListe {
  /** Sites d'annonce retenus ; vide = tous. */
  sources: string[];
  cv: CritereCv;
  /** Villes retenues par leur clé (`cleVille`) ; vide = toutes. */
  villes: string[];
  /** Adéquations retenues ; vide = toutes. */
  adequations: Adequation[];
  /** Pastille minimale ; null = toutes, pastille absente comprise. */
  pastilleMin: number | null;
  /**
   * « Joignable sur WhatsApp » (lot 6). Un **booléen**, jamais le numéro : un
   * lecteur peut donc filtrer là-dessus sans qu'aucune coordonnée ne lui soit
   * servie. `false` = pas de filtre.
   */
  whatsapp: boolean;
}

export const CRITERES_VIDES: CriteresListe = {
  sources: [],
  cv: 'tous',
  villes: [],
  adequations: [],
  pastilleMin: null,
  whatsapp: false,
};

/** Clé de comparaison d'une ville : casse, accents et blancs ignorés. */
export function cleVille(ville: string | null | undefined): string {
  const propre = (ville ?? '')
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .trim();
  return propre || VILLE_NON_RENSEIGNEE;
}

export interface ChoixVille {
  cle: string;
  libelle: string;
  nombre: number;
}

/** Villes présentes dans une liste, la plus fréquente d'abord ; « Non renseignée » en dernier. */
export function villesDe(liste: readonly CandidatureResume[]): ChoixVille[] {
  const parCle = new Map<string, ChoixVille>();
  for (const c of liste) {
    const cle = cleVille(c.ville);
    const choix = parCle.get(cle);
    if (choix) choix.nombre += 1;
    else parCle.set(cle, { cle, libelle: cle === VILLE_NON_RENSEIGNEE ? 'Non renseignée' : c.ville!.trim(), nombre: 1 });
  }
  return [...parCle.values()].sort(
    (a, b) =>
      Number(a.cle === VILLE_NON_RENSEIGNEE) - Number(b.cle === VILLE_NON_RENSEIGNEE) ||
      b.nombre - a.nombre ||
      a.libelle.localeCompare(b.libelle, 'fr'),
  );
}

export function critereActif(criteres: CriteresListe): boolean {
  return (
    criteres.sources.length > 0 ||
    criteres.cv !== 'tous' ||
    criteres.villes.length > 0 ||
    criteres.adequations.length > 0 ||
    criteres.pastilleMin !== null ||
    criteres.whatsapp
  );
}

export function correspondAuxCriteres(criteres: CriteresListe, c: CandidatureResume): boolean {
  if (criteres.sources.length > 0 && !criteres.sources.includes(c.source)) return false;
  if (criteres.cv === 'avec' && !c.cvJoint) return false;
  if (criteres.cv === 'sans' && c.cvJoint) return false;
  if (criteres.villes.length > 0 && !criteres.villes.includes(cleVille(c.ville))) return false;
  if (criteres.adequations.length > 0 && (!c.adequation || !criteres.adequations.includes(c.adequation))) {
    return false;
  }
  if (criteres.pastilleMin !== null && (c.potentiel === null || c.potentiel < criteres.pastilleMin)) return false;
  if (criteres.whatsapp && !c.joignableWhatsapp) return false;
  return true;
}

const liste = (valeur: string | null) =>
  (valeur ?? '')
    .split(',')
    .map((v) => v.trim())
    .filter(Boolean);

/**
 * Critères lus dans une adresse (`site`, `cv`, `ville`, `adequation`,
 * `pastille`). Une valeur inconnue est ignorée, jamais une erreur : un lien
 * ancien reste ouvrable.
 */
export function criteresDepuisRecherche(lire: (cle: string) => string | null): CriteresListe {
  const cv = lire('cv');
  const pastille = /^[0-5]$/.test(lire('pastille') ?? '') ? Number(lire('pastille')) : null;
  return {
    sources: [...new Set(liste(lire('site')).filter((s) => /^[a-z_]{1,30}$/.test(s)))],
    cv: (CRITERES_CV as readonly string[]).includes(cv ?? '') ? (cv as CritereCv) : 'tous',
    villes: [...new Set(liste(lire('ville')).map((v) => (v === VILLE_NON_RENSEIGNEE ? v : cleVille(v))))].slice(0, 50),
    adequations: [...new Set(liste(lire('adequation')))].filter((a): a is Adequation =>
      (ADEQUATIONS as readonly string[]).includes(a),
    ),
    pastilleMin: pastille,
    whatsapp: lire('whatsapp') === '1',
  };
}

/** Paires clé-valeur d'adresse des critères actifs, dans un ordre stable. */
export function criteresVersRecherche(criteres: CriteresListe): Array<[string, string]> {
  const paires: Array<[string, string]> = [];
  if (criteres.sources.length) paires.push(['site', criteres.sources.join(',')]);
  if (criteres.cv !== 'tous') paires.push(['cv', criteres.cv]);
  if (criteres.villes.length) paires.push(['ville', criteres.villes.join(',')]);
  if (criteres.adequations.length) paires.push(['adequation', criteres.adequations.join(',')]);
  if (criteres.pastilleMin !== null) paires.push(['pastille', String(criteres.pastilleMin)]);
  if (criteres.whatsapp) paires.push(['whatsapp', '1']);
  return paires;
}

/** Résumé lisible des critères, pour l'en-tête du classeur exporté. */
export function decrireCriteres(criteres: CriteresListe, villes: readonly ChoixVille[] = []): string[] {
  const parties: string[] = [];
  if (criteres.sources.length) {
    parties.push(`site : ${criteres.sources.map((s) => LIBELLES_SOURCE[s] ?? s).join(', ')}`);
  }
  if (criteres.cv !== 'tous') parties.push(LIBELLES_CRITERE_CV[criteres.cv]);
  if (criteres.villes.length) {
    const libelle = (cle: string) =>
      villes.find((v) => v.cle === cle)?.libelle ?? (cle === VILLE_NON_RENSEIGNEE ? 'Non renseignée' : cle);
    parties.push(`ville : ${criteres.villes.map(libelle).join(', ')}`);
  }
  if (criteres.adequations.length) {
    parties.push(`adéquation : ${criteres.adequations.map((a) => LIBELLES_ADEQUATION[a]).join(', ')}`);
  }
  if (criteres.pastilleMin !== null) parties.push(`pastille ${criteres.pastilleMin} ou plus`);
  if (criteres.whatsapp) parties.push('joignable sur WhatsApp');
  return parties;
}
